import React, { CSSProperties, memo, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import VideoPlayer from '../QueuePanel/VideoPlayer/VideoPlayer';
import { useGetQueueVideoPlaylist } from '../../../hooks/MonitorQueue/useGetQueueVideoPlaylist';
import { baseSoundURL } from '../../../config';
import { TerminalTabloElementSettings } from '../../../types/Admin/TerminalAppearanceTypes';
import { videoVolume as defaultVideoVolume } from '../../../constants/API';

type TabloVideoLayerProps = {
  videoElement?: TerminalTabloElementSettings | null;
  layoutMode: boolean;
  videoHost: HTMLElement | null;
  isPauseVideo: boolean;
  videoFullScreen: boolean;
  videoVolumeOverride?: number | null;
};

const normalizeUrls = (urls?: string[] | null) => {
  if (!urls?.length) return [] as string[];
  return urls.map((url) => {
    if (!url) return '';
    if (/^https?:\/\//i.test(url) || url.startsWith('//')) return url;
    const separator = url.startsWith('/') ? '' : '/';
    return `${baseSoundURL}${separator}${url}`;
  });
};

/**
 * Isolated from QueuePanel re-renders (ticket polling).
 * Mounts VideoPlayer either into a host slot (legacy grid) or as absolute layer (layout mode).
 */
const TabloVideoLayer = ({
  videoElement,
  layoutMode,
  videoHost,
  isPauseVideo,
  videoFullScreen,
  videoVolumeOverride
}: TabloVideoLayerProps) => {
  const [playlistFromApi, setPlaylistFromApi] = useState<string[]>([]);
  useGetQueueVideoPlaylist(setPlaylistFromApi);

  const isVideoEnabled = videoElement?.isEnabled ?? true;
  const videoUrlsKey = (videoElement?.videoUrls ?? []).join('\0');
  const tabloUrls = useMemo(
    () => normalizeUrls(videoElement?.videoUrls),
    [videoUrlsKey]
  );
  const playlist = useMemo(
    () => (tabloUrls.length > 0 ? tabloUrls : playlistFromApi),
    [tabloUrls, playlistFromApi]
  );
  const hasActiveVideos = playlist.length > 0;
  const volume = Number.isFinite(videoVolumeOverride)
    ? Math.min(1, Math.max(0, Math.round(((videoVolumeOverride as number) / 100) * 100) / 100))
    : defaultVideoVolume;

  const layoutStyle = useMemo<CSSProperties>(() => {
    if (!layoutMode) return { width: '100%', height: '100%' };
    if (videoFullScreen) {
      return {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        zIndex: 10,
        transform: 'translateZ(0)',
        contain: 'strict'
      };
    }
    const x = Number.isFinite(Number(videoElement?.x)) ? Number(videoElement?.x) : 0;
    const y = Number.isFinite(Number(videoElement?.y)) ? Number(videoElement?.y) : 0;
    const w = Number.isFinite(Number(videoElement?.width)) ? Number(videoElement?.width) : 60;
    const h = Number.isFinite(Number(videoElement?.height)) ? Number(videoElement?.height) : 55;
    return {
      position: 'absolute',
      left: `${x}%`,
      top: `${y}%`,
      width: `${w}%`,
      height: `${h}%`,
      zIndex: 2,
      backgroundColor: videoElement?.backgroundColor || '#000',
      transform: 'translateZ(0)',
      contain: 'layout paint style',
      overflow: 'hidden'
    };
  }, [
    layoutMode,
    videoFullScreen,
    videoElement?.x,
    videoElement?.y,
    videoElement?.width,
    videoElement?.height,
    videoElement?.backgroundColor
  ]);

  if (!isVideoEnabled || !hasActiveVideos) {
    return null;
  }

  const player = (
    <VideoPlayer
      videoPlaylist={playlist}
      isPauseVideo={isPauseVideo}
      volume={volume}
      noRoundedCorners={videoFullScreen || layoutMode}
    />
  );

  if (layoutMode) {
    return <div style={layoutStyle}>{player}</div>;
  }

  if (!videoHost) {
    return null;
  }

  return createPortal(<div style={{ width: '100%', height: '100%' }}>{player}</div>, videoHost);
};

const layoutKey = (el?: TerminalTabloElementSettings | null) =>
  el
    ? `${el.isEnabled}|${el.x}|${el.y}|${el.width}|${el.height}|${el.backgroundColor}|${(
        el.videoUrls ?? []
      ).join(',')}`
    : '';

export default memo(TabloVideoLayer, (prev, next) => {
  return (
    prev.layoutMode === next.layoutMode &&
    prev.isPauseVideo === next.isPauseVideo &&
    prev.videoFullScreen === next.videoFullScreen &&
    prev.videoVolumeOverride === next.videoVolumeOverride &&
    prev.videoHost === next.videoHost &&
    layoutKey(prev.videoElement) === layoutKey(next.videoElement)
  );
});
